import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

/**
 * Gespreksverslag uit Fathom (opdracht Frank 3 november 2026): deelgrootte en tijdslimiet per modelaanroep als instelling,
 * zodat een sneller model grotere delen kan nemen. Leeg is de standaard van de server (die staat als voorbeeld in het
 * veld); opslaan stuurt 0 voor leeg, en de server zet het dan terug op de standaard.
 *
 * BESLUIT FRANK 3 NOV (uitrol 0.9.238): het verslag staat STANDAARD UIT. Uit neemt een verslagvraag de gewone weg van
 * de chat, zoals in 0.9.237. Het vakje zet het voor dit account aan of uit en bewaart dat meteen.
 */
export function alsGetal(waarde) {
  const n = parseInt(String(waarde ?? "").trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export default function VerslagInstelling() {
  const { t } = useTaal();
  const [tekens, setTekens] = useState("");
  const [seconden, setSeconden] = useState("");
  const [standaard, setStandaard] = useState({ tekens: 0, seconden: 0 });
  const [aan, setAan] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then(({ data }) => {
      setTekens(data.verslag_deel_tekens ? String(data.verslag_deel_tekens) : "");
      setSeconden(data.verslag_seconden ? String(data.verslag_seconden) : "");
      setStandaard({ tekens: data.verslag_standaard_tekens || 0, seconden: data.verslag_standaard_seconden || 0 });
      setAan(data.gespreksverslag_aan === true);
    }).catch(() => {});
  }, []);

  async function zetAan(nieuw) {
    try {
      const { data } = await axios.put(`${API}/settings`, { gespreksverslag_aan: nieuw });
      setAan(data?.gespreksverslag_aan === true);
      toast.success(t("Opgeslagen"));
    } catch {
      toast.error(t("Niet opgeslagen"));
    }
  }

  async function bewaar(e) {
    e.preventDefault();
    try {
      await axios.put(`${API}/settings`, { verslag_deel_tekens: alsGetal(tekens), verslag_seconden: alsGetal(seconden) });
      toast.success(t("Opgeslagen"));
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? t(detail) : t("Niet opgeslagen"));
    }
  }

  return (
    <form onSubmit={bewaar} className="space-y-2" data-testid="verslag-instelling">
      <p className="text-sm font-medium">{t("Gespreksverslag uit Fathom")}</p>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={aan} onChange={(e) => zetAan(e.target.checked)} data-testid="verslag-aan" />
        {t("Een verslagvraag maakt een gespreksverslag uit Fathom (standaard uit)")}
      </label>
      <div className="flex flex-wrap gap-2">
        <Input type="number" min="0" value={tekens} onChange={(e) => setTekens(e.target.value)}
               placeholder={standaard.tekens ? String(standaard.tekens) : ""} aria-label={t("Tekens per deel")}
               className="h-8 w-32 text-sm" data-testid="verslag-tekens" />
        <Input type="number" min="0" value={seconden} onChange={(e) => setSeconden(e.target.value)}
               placeholder={standaard.seconden ? String(standaard.seconden) : ""} aria-label={t("Seconden per aanroep")}
               className="h-8 w-32 text-sm" data-testid="verslag-seconden" />
        <Button type="submit" size="sm" data-testid="verslag-opslaan">{t("Verslag opslaan")}</Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {t("Tekens transcript per modelaanroep en de tijdslimiet per aanroep. Leeg is de standaard; met een sneller model kunnen de delen groter.")}
      </p>
    </form>
  );
}
